/**
 * Journey A — "Needs your action" card (upgraded from the Phase 8-F pending
 * proposals card).
 *
 * Surfaces the proposals the CURRENT user can act on: a pending count plus the
 * latest 3–5, each with shop, platform, tool, and a risk pill. Reviewers with
 * `proposals.approve` get inline Approve / Deny right on the row; everyone else
 * gets a "Review →" link to `/actions/queue`. When the queue is clear we show a
 * friendly "all caught up" state instead of a bare line.
 *
 * Data source (unchanged): the employee-scoped proposals API. The backend
 * already scopes results to the shops the employee can act on, so the list is
 * exactly "what's waiting on you".
 *   GET /api/v2/employee/tara/proposals?status=pending_review&per_page=5
 *
 * "Review all" goes to `/actions/queue` (the legacy in-app proposals page).
 *
 * The backend accepts a single `status` value at a time, so `pending_review`
 * and the legacy `proposed` fallback are fetched separately and merged.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SimpleCard from '../../ui/cards/SimpleCard';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import { tickWithCircleSVG } from '../../lib/allCustomSVGs';
import { setMessage } from '../../store/reducers/appSlice';
import { can, useEmployeeRole } from '../../lib/rbac';
import {
  Proposal,
  approveProposal,
  denyProposal,
  listProposals,
} from '../../lib/api/proposals/proposals-api';
import '../../styles/pages/dashboard/dashboardAgencyCards.scss';

const PLATFORM_LABELS: Record<string, string> = {
  facebook_ads: 'Facebook Ads',
  facebook: 'Facebook Ads',
  google_ads: 'Google Ads',
  tiktok_ads: 'TikTok Ads',
  tiktok: 'TikTok Ads',
  linkedin_ads: 'LinkedIn Ads',
  linkedin: 'LinkedIn Ads',
  ga4: 'GA4',
  gsc: 'Search Console',
};

const platformName = (p: string): string =>
  PLATFORM_LABELS[p] || (p ? p.replace(/_/g, ' ') : '—');

const toolLabelOf = (p: Proposal): string => {
  const raw = p.tool_name || p.action_type?.name || p.action_type_id || '';
  return raw
    ? raw.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    : 'Action';
};

const PendingProposalsCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const role = useEmployeeRole();
  const canApprove = can(role, 'proposals.approve');
  const canDeny = can(role, 'proposals.deny');

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  // ids currently being approved/denied — disables that row's buttons
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // Backend accepts a single `status` value at a time. `pending_review`
      // matches the agency-portal dashboard; treat `proposed` as a fallback
      // for older rows that have not yet been transitioned.
      const [pendingRes, proposedRes] = await Promise.all([
        listProposals({ status: 'pending_review', per_page: 5, page: 1 }),
        listProposals({ status: 'proposed', per_page: 5, page: 1 }).catch(
          () => ({ data: [], meta: { total: 0 } as any })
        ),
      ]);

      const combined: Proposal[] = [
        ...(pendingRes.data || []),
        ...(proposedRes.data || []),
      ]
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        .slice(0, 5);

      const totalCount =
        (pendingRes.meta?.total ?? pendingRes.data?.length ?? 0) +
        (proposedRes.meta?.total ?? proposedRes.data?.length ?? 0);

      setProposals(combined);
      setTotal(totalCount);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PendingProposalsCard] load failed', err);
      setError(true);
      setProposals([]);
      setTotal(0);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const formatAge = (createdAt: string): string => {
    const created = new Date(createdAt);
    const ageMin = Math.max(
      0,
      Math.round((Date.now() - created.getTime()) / 60000)
    );
    if (ageMin < 60) return `${ageMin}m ago`;
    if (ageMin < 24 * 60) return `${Math.round(ageMin / 60)}h ago`;
    return `${Math.round(ageMin / (60 * 24))}d ago`;
  };

  const handleApprove = async (p: Proposal) => {
    if (actingId) return;
    setActingId(p.id);
    try {
      await approveProposal(p.id, undefined);
      dispatch(
        setMessage({
          message: t('ActionApprovedSuccessfully'),
          messageType: 'success',
        })
      );
      await load();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[PendingProposalsCard] approve failed', err);
      const msg =
        err?.status === 409
          ? t('ProposalAlreadyProcessed')
          : err?.message || t('FailedToApproveAction');
      dispatch(setMessage({ message: msg, messageType: 'error' }));
    } finally {
      setActingId(null);
    }
  };

  const handleDeny = async (p: Proposal) => {
    if (actingId) return;
    const reason = window.prompt(t('DenyReasonPrompt') as string);
    // Cancelled prompt — abort silently.
    if (reason === null) return;
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      dispatch(
        setMessage({ message: t('DenyReasonRequired'), messageType: 'error' })
      );
      return;
    }
    setActingId(p.id);
    try {
      await denyProposal(p.id, trimmed);
      dispatch(
        setMessage({
          message: t('ActionDeniedSuccessfully'),
          messageType: 'success',
        })
      );
      await load();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[PendingProposalsCard] deny failed', err);
      const msg =
        err?.status === 409
          ? t('ProposalAlreadyProcessed')
          : err?.message || t('FailedToDenyAction');
      dispatch(setMessage({ message: msg, messageType: 'error' }));
    } finally {
      setActingId(null);
    }
  };

  return (
    <SimpleCard grow={false}>
      <div className='pendingProposalsCard'>
        <div className='pendingProposalsCard__header'>
          <div className='pendingProposalsCard__header-title'>
            <h3>{t('NeedsYourActionTitle')}</h3>
            {total > 0 && (
              <span className='pendingProposalsCard__header-title-badge'>
                {total}
              </span>
            )}
          </div>
          <Link
            to='/actions/queue'
            className='pendingProposalsCard__header-link'
          >
            {t('ReviewAll')}
          </Link>
        </div>

        {loading && (
          <div className='pendingProposalsCard__loading'>
            <LoadingSpinner />
          </div>
        )}

        {!loading && error && (
          <div className='pendingProposalsCard__empty'>
            {t('NeedsYourActionLoadError')}
          </div>
        )}

        {!loading && !error && proposals.length === 0 && (
          <div className='pendingProposalsCard__caughtUp'>
            <span
              className='pendingProposalsCard__caughtUp-icon'
              aria-hidden='true'
            >
              <CustomSVGs svg={tickWithCircleSVG} />
            </span>
            <span className='pendingProposalsCard__caughtUp-text'>
              {t('NeedsYourActionEmpty')}
            </span>
          </div>
        )}

        {!loading && !error && proposals.length > 0 && (
          <div className='pendingProposalsCard__list'>
            {proposals.map((p) => {
              const shopLabel =
                p.shop?.name || p.shop?.slug || `shop #${p.shop_id}`;
              const acting = actingId === p.id;
              return (
                <div key={p.id} className='pendingProposalsCard__row'>
                  <div className='pendingProposalsCard__row-main'>
                    <div className='pendingProposalsCard__row-title'>
                      {toolLabelOf(p)}
                      <span
                        className={`risk-pill risk-pill--${p.risk_level}`}
                      >
                        {p.risk_level.toUpperCase()}
                      </span>
                    </div>
                    <div className='pendingProposalsCard__row-meta'>
                      {shopLabel} · {platformName(p.platform)} ·{' '}
                      {formatAge(p.created_at)}
                    </div>
                  </div>
                  <div className='pendingProposalsCard__row-actions'>
                    {canApprove ? (
                      <>
                        <button
                          type='button'
                          className='pendingProposalsCard__btn pendingProposalsCard__btn--approve'
                          disabled={acting}
                          onClick={() => handleApprove(p)}
                        >
                          {t('Approve')}
                        </button>
                        {canDeny && (
                          <button
                            type='button'
                            className='pendingProposalsCard__btn pendingProposalsCard__btn--deny'
                            disabled={acting}
                            onClick={() => handleDeny(p)}
                          >
                            {t('Deny')}
                          </button>
                        )}
                      </>
                    ) : (
                      <Link
                        to='/actions/queue'
                        className='pendingProposalsCard__btn pendingProposalsCard__btn--review'
                      >
                        {t('Review')}
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SimpleCard>
  );
};

export default PendingProposalsCard;
